import React, { useState } from "react";
import { Trash2, RotateCcw, Trash, AlertTriangle, Calendar, User } from "lucide-react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";

interface PapirkurvProps extends ComponentProps {
  deletedCases: Case[];
  setDeletedCases: (cases: Case[]) => void;
}

const Papirkurv: React.FC<PapirkurvProps> = ({
  cases,
  setCases,
  deletedCases,
  setDeletedCases,
}) => {
  const [isRestoring, setIsRestoring] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);

  const handleRestore = async (caseId: number) => {
    const caseToRestore = deletedCases.find(c => c.id === caseId);
    if (!caseToRestore) return;

    const confirm = window.confirm(
      `♻️ GJENOPPRETT ORDRE\n\n` +
      `Vil du gjenopprette ordre #${caseId} for ${caseToRestore.kunde}?\n\n` +
      `Ordren vil bli flyttet tilbake til aktive ordre.`
    );

    if (!confirm) return;

    setIsRestoring(caseId);

    try {
      const success = await supabaseService.restoreCaseFromTrash(caseId);
      
      if (success) {
        // Fjern fra papirkurv
        setDeletedCases(deletedCases.filter(c => c.id !== caseId));
        
        // Last inn cases på nytt for å få den gjenopprettede
        const loadedCases = await supabaseService.loadCases();
        setCases(loadedCases);
        
        alert(`✅ Ordre #${caseId} gjenopprettet!`);
      } else {
        alert(`❌ Kunne ikke gjenopprette ordre. Prøv igjen.`);
      }
    } catch (error) {
      console.error('Feil ved gjenoppretting:', error);
      alert(`❌ Feil ved gjenoppretting. Prøv igjen.`);
    } finally {
      setIsRestoring(null);
    }
  };

  const handlePermanentDelete = async (caseId: number) => {
    const caseToDelete = deletedCases.find(c => c.id === caseId);
    if (!caseToDelete) return;

    const confirm = window.confirm(
      `🔥 PERMANENT SLETTING\n\n` +
      `ADVARSEL: Dette kan ikke angres!\n\n` +
      `Vil du permanent slette ordre #${caseId} for ${caseToDelete.kunde}?\n\n` +
      `Ordren vil bli slettet for alltid og kan ikke gjenopprettes.`
    );

    if (!confirm) return;

    setIsDeleting(caseId);

    try {
      const success = await supabaseService.permanentlyDeleteCase(caseId);
      
      if (success) {
        setDeletedCases(deletedCases.filter(c => c.id !== caseId));
        alert(`✅ Ordre #${caseId} permanent slettet.`);
      } else {
        alert(`❌ Kunne ikke slette ordre. Prøv igjen.`);
      }
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert(`❌ Feil ved sletting. Prøv igjen.`);
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-4 border-b bg-gradient-to-r from-red-50 to-orange-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Trash2 className="text-red-600" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Papirkurv</h2>
              <p className="text-sm text-gray-600">Slettede ordre beholdes i 30 dager</p>
            </div>
            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
              {deletedCases.length} ordre
            </span>
          </div>
        </div>
      </div>

      <div className="p-4">
        {deletedCases.length === 0 ? (
          <div className="text-center py-12">
            <Trash2 className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Papirkurven er tom
            </h3>
            <p className="text-gray-600">
              Slettede ordre vil vises her og kan gjenopprettes i 30 dager
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="text-yellow-600 mt-0.5" size={18} />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Automatisk sletting</p>
                  <p>Ordre som har vært i papirkurven i mer enn 30 dager slettes automatisk og permanent.</p>
                </div>
              </div>
            </div>

            {deletedCases.map((case_) => (
              <div key={case_.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">#{case_.id} - {case_.kunde}</h3>
                    <p className="text-sm text-gray-600">{case_.maskinMerke}</p>
                    <p className="text-xs text-gray-500 mt-1">{case_.beskrivelse}</p>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <span className="flex items-center space-x-1">
                      <Calendar size={12} />
                      <span>Slettet: {new Date(case_.opprettet).toLocaleDateString('nb-NO')}</span>
                    </span>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleRestore(case_.id)}
                      disabled={isRestoring === case_.id}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <RotateCcw size={14} />
                      <span>{isRestoring === case_.id ? 'Gjenoppretter...' : 'Gjenopprett'}</span>
                    </button>
                    
                    <button
                      onClick={() => handlePermanentDelete(case_.id)}
                      disabled={isDeleting === case_.id}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      <Trash size={14} />
                      <span>{isDeleting === case_.id ? 'Sletter...' : 'Slett permanent'}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Papirkurv;